import Utils from '@service-victoria/component-library/utils';
import axios, { AxiosError, AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';

export const logError: (error?: AxiosError) => void = (error = undefined) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  if (!error) {
    return undefined;
  }
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error(error.response.data);
    console.error(error.response.status);
    console.error(error.response.headers);
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    console.error(error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error: ', error.message);
  }
};

/**
 * Type-guard function to determine of given object isntanceOf AxiosError
 * @param err
 */
export function isError(err: AxiosError): err is AxiosError {
  return err &&
    err.response &&
    err.response.data &&
    err.response.status &&
    err.response.headers &&
    err.request
    ? true
    : false;
}

/**
 * Wrapper for accessing and managing API requests (Get/Post/Put/Delete)
 */
export class Adapter {
  /**
   * Reference to an instance of the api
   */
  private instance: AxiosInstance;

  constructor(config: AxiosRequestConfig) {
    // Use these instances of axios to make network requests to the testservice API's
    this.instance = axios.create(config);
  }

  updateConfig = (config: AxiosRequestConfig): void => {
    this.instance.defaults = config;
  };

  async getValue<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.instance.get(url, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new Utils.errors.SVError(JSON.stringify(error)));
    }
  }

  async postValue<T>(
    url: string,
    body: any, // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return this.instance.post(url, body, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new Utils.errors.SVError(JSON.stringify(error)));
    }
  }

  async putValue<T>(
    url: string,
    body: any, // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      return this.instance.put(url, body, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new Utils.errors.SVError(JSON.stringify(error)));
    }
  }

  async deleteValue<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return this.instance.delete(url, config);
    } catch (error) {
      logError(error);
      return Promise.reject(new Utils.errors.SVError(JSON.stringify(error)));
    }
  }
}
